<script>
	import model from "../../stores/modelStore";
	import { projectSettings } from "../../config";
	import { isValid } from "../../validation";

	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import Select from "../../components/Select.svelte";
	import RadioGroup from "../../components/RadioGroup.svelte";
	import DatePicker from "../../components/DatePicker.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import CheckboxButton from "../../components/CheckboxButton.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";

	import Impact from "./Impact.svelte";
	import OtherResponsibilities from "./OtherResponsibilities.svelte";
	import MissedEvents from "./MissedEvents.svelte";

	export let pageTitle = null;
	export let disabled = null;

	let buttons = null;

	if (!disabled) {
		document.title = pageTitle;
	}

	const cleanSurveyData = () => {
		if ($model.receiveBreaks !== "Yes") {
			$model.dateReceiveBreaks = null;
		}

		if ($model.breaksChange !== "Yes") {
			$model.breaksChangeWhy = null;
			$model.breaksChangeDetails = null;
		}

		if ($model.breaksChangeWhy !== "Other") {
			$model.breaksChangeDetails = null;
		}

		if ($model.askForBreaks !== "Yes") {
			$model.askForBreaksResponse = null;
		}

		if (!$model.impactDistress) {
			$model.impactDistressDays = null;
			$model.impactDistressSeverity = null;
		}

		if (!$model.impactAnxiety) {
			$model.impactAnxietyDays = null;
			$model.impactAnxietySeverity = null;
		}

		if (!$model.impactFrustration) {
			$model.impactFrustrationDays = null;
			$model.impactFrustrationSeverity = null;
		}

		if (!$model.impactDisappointment) {
			$model.impactDisappointmentDays = null;
			$model.impactDisappointmentSeverity = null;
		}

		if (!$model.impactFatigue) {
			$model.impactFatigueDays = null;
			$model.impactFatigueSeverity = null;
		}

		if (!$model.impactOther) {
			$model.impactOtherDetails = null;
			$model.impactOtherSeverity = null;
		}

		if ($model.otherResponsibilities !== "Yes") {
			$model.study = $model.studyHours = null;
			$model.otherWorkPaid = $model.otherWorkPaidHours = null;
			$model.volunteer = $model.volunteerHours = null;
			$model.family = $model.familyHours = null;
		} else {
			if (!$model.study) {
				$model.studyHours = null;
			}
			if (!$model.otherWorkPaid) {
				$model.otherWorkPaidHours = null;
			}
			if (!$model.volunteer) {
				$model.volunteerHours = null;
			}
			if (!$model.family) {
				$model.familyHours = null;
			}
		}

		if ($model.missEvent !== "Yes") {
			$model.social = $model.socialHowOften = null;
			$model.otherWork = $model.otherWorkHowOften = null;
			$model.studyCommitment = $model.studyCommitmentHowOften = null;
		} else {
			if (!$model.social) {
				$model.socialHowOften = null;
			}
			if (!$model.otherWork) {
				$model.otherWorkHowOften = null;
			}
			if (!$model.studyCommitment) {
				$model.studyCommitmentHowOften = null;
			}
		}

		if (!$model.describeOther) {
			$model.describeOtherDetails = null;
		}

		if (!$model.feelOther) {
			$model.feelOtherDetails = null;
		}
	};

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let errors = [...isValid()];

		if (!errors.length) {
			cleanSurveyData();
			buttons.next();
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};
</script>

<PageSection>
	<Form {disabled} id="form-survey" on:submit={handleSubmit}>
		<FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Your Work Experience at KFC</h1>
			</FormColumn>
		</FormRow>

		<p class="confirmation-hide">
			The purpose of this survey is for us to gain a better understanding of how not being given rest breaks
			affected you from a physical and mental health perspective. All answers to the survey will be kept
			confidential.
		</p>

		<hr class="confirmation-hide primary" />

		<FormRow>
			<FormColumn>
				<YesNoGroup
					required
					{disabled}
					name="receiveBreaks"
					label="Did you begin to receive 10-minute breaks part way through your employment?"
					bind:value={$model.receiveBreaks} />
			</FormColumn>
		</FormRow>

		{#if $model.receiveBreaks === "Yes"}
			<FormRow>
				<FormColumn>
					<DatePicker
						required
						{disabled}
						label="When did you start to receive 10 minute paid rest breaks (approximately)?"
						validator={true}
						start={new Date(2000, 0, 1)}
						end={new Date()}
						placeholder="e.g. 01/05/2021"
						bind:formattedSelected={$model.dateReceiveBreaks}
						openOnClass="calendar-icon" />
				</FormColumn>
			</FormRow>
		{/if}

		<FormRow>
			<FormColumn>
				<YesNoGroup
					required
					{disabled}
					name="breaksChange"
					label="Did the way you receive rest breaks change throughout your employment at KFC? For example, when Lifelenz was implemented did you begin receiving rest breaks?"
					bind:value={$model.breaksChange} />
			</FormColumn>
		</FormRow>

		{#if $model.breaksChange === "Yes"}
			<FormRow>
				<FormColumn>
					<Select
						required
						{disabled}
						label="What contributed to this change?"
						id="classification"
						bind:value={$model.breaksChangeWhy}
						optionList={[
							{ label: "Change in management", value: "Change in management" },
							{ label: "Change in systems", value: "Change in systems" },
							{ label: "Change in attitudes", value: "Change in attitudes" },
							{ label: "Change in level of compliance", value: "Change in level of compliance" },
							{ label: "Other", value: "Other" }
						]}
						textProperty="label"
						valueProperty="value" />
				</FormColumn>
			</FormRow>

			{#if $model.breaksChangeWhy === "Other"}
				<FormRow>
					<FormColumn>
						<TextArea
							{disabled}
							id="breaksChangeDetails"
							label="Please provide further information"
							bind:value={$model.breaksChangeDetails} />
					</FormColumn>
				</FormRow>
			{/if}
		{/if}

		<FormRow>
			<FormColumn>
				<RadioGroup
					required
					{disabled}
					name="askForBreaks"
					label="Did you ever ask for a 10 minute rest break?"
					bind:value={$model.askForBreaks}
					optionList={[
						{ label: "Yes", value: "Yes" },
						{ label: "No", value: "No" },
						{ label: "Don't know/can't recall", value: "Unsure" }
					]} />
			</FormColumn>
		</FormRow>

		{#if $model.askForBreaks === "Yes"}
			<FormRow>
				<FormColumn>
					<TextArea
						required
						{disabled}
						id="askForBreaksResponse"
						label="What was the response?"
						bind:value={$model.askForBreaksResponse} />
				</FormColumn>
			</FormRow>
		{/if}

		<FormRow>
			<FormColumn>
				<p class="label bold mb-1">
					What impact did not receiving a rest break have on your wellbeing (tick all that apply)
				</p>
			</FormColumn>
		</FormRow>

		<div class="impact">
			<Impact
				required
				{disabled}
				impactType="Distress"
				bind:impact={$model.impactDistress}
				bind:impactDays={$model.impactDistressDays}
				bind:impactSeverity={$model.impactDistressSeverity} />

			<Impact
				required
				{disabled}
				impactType="Anxiety"
				bind:impact={$model.impactAnxiety}
				bind:impactDays={$model.impactAnxietyDays}
				bind:impactSeverity={$model.impactAnxietySeverity} />

			<Impact
				required
				{disabled}
				impactType="Frustration"
				bind:impact={$model.impactFrustration}
				bind:impactDays={$model.impactFrustrationDays}
				bind:impactSeverity={$model.impactFrustrationSeverity} />

			<Impact
				required
				{disabled}
				impactType="Disappointment"
				bind:impact={$model.impactDisappointment}
				bind:impactDays={$model.impactDisappointmentDays}
				bind:impactSeverity={$model.impactDisappointmentSeverity} />

			<Impact
				required
				{disabled}
				impactType="Fatigue"
				bind:impact={$model.impactFatigue}
				bind:impactDays={$model.impactFatigueDays}
				bind:impactSeverity={$model.impactFatigueSeverity} />

			<Impact
				required
				{disabled}
				impactOther={true}
				impactType="Other"
				bind:impact={$model.impactOther}
				bind:impactOtherDetails={$model.impactOtherDetails}
				bind:impactSeverity={$model.impactOtherSeverity} />
		</div>

		<FormRow>
			<FormColumn>
				<YesNoGroup
					required
					{disabled}
					name="otherResponsibilities"
					label="At the time you were working at KFC, did you have other work or educational responsibilities which occupied your time?"
					bind:value={$model.otherResponsibilities} />
			</FormColumn>
		</FormRow>

		{#if $model.otherResponsibilities === "Yes"}
			<OtherResponsibilities
				required
				{disabled}
				responsibilityType="Study"
				bind:responsibility={$model.study}
				bind:responsibilityHours={$model.studyHours} />

			<OtherResponsibilities
				required
				{disabled}
				responsibilityType="Other work (paid)"
				bind:responsibility={$model.otherWorkPaid}
				bind:responsibilityHours={$model.otherWorkPaidHours} />

			<OtherResponsibilities
				required
				{disabled}
				responsibilityType="Other work (volunteer)"
				bind:responsibility={$model.volunteer}
				bind:responsibilityHours={$model.volunteerHours} />

			<OtherResponsibilities
				required
				{disabled}
				responsibilityType="Family responsibilities"
				bind:responsibility={$model.family}
				bind:responsibilityHours={$model.familyHours} />
		{/if}

		<FormRow>
			<FormColumn>
				<YesNoGroup
					required
					{disabled}
					name="missEvent"
					label="Did you ever cancel or miss events or deadlines because of the effects of not receiving 10 minute rest breaks?"
					bind:value={$model.missEvent} />
			</FormColumn>
		</FormRow>

		{#if $model.missEvent === "Yes"}
			<MissedEvents
				required
				{disabled}
				missedEventType="Social events"
				bind:missedEvent={$model.social}
				bind:howOften={$model.socialHowOften} />

			<MissedEvents
				required
				{disabled}
				missedEventType="Other work commitments"
				bind:missedEvent={$model.otherWork}
				bind:howOften={$model.otherWorkHowOften} />

			<MissedEvents
				required
				{disabled}
				missedEventType="Study commitments"
				bind:missedEvent={$model.studyCommitment}
				bind:howOften={$model.studyCommitmentHowOften} />
		{/if}

		<FormRow>
			<FormColumn cols="12" class="mb-1">
				<p class="label bold">Generally, how would you describe your KFC restaurant? (tick all that apply)</p>
			</FormColumn>
			<FormColumn cols="12" class="mb-1">
				<CheckboxButton {disabled} id="describe-hot" label="Hot" bind:checked={$model.describeHot} />
			</FormColumn>
			<FormColumn cols="12" class="mb-1">
				<CheckboxButton {disabled} id="describe-stuffy" label="Stuffy" bind:checked={$model.describeStuffy} />
			</FormColumn>
			<FormColumn cols="12" class="mb-1">
				<CheckboxButton
					{disabled}
					id="describe-uncomfortable"
					label="Uncomfortable"
					bind:checked={$model.describeUncomfortable} />
			</FormColumn>
			<FormColumn cols="12" class="mb-1">
				<CheckboxButton {disabled} id="describe-busy" label="Busy" bind:checked={$model.describeBusy} />
			</FormColumn>
			<FormColumn cols="12" class="mb-1">
				<CheckboxButton {disabled} id="describe-Other" label="Other" bind:checked={$model.describeOther} />
			</FormColumn>
		</FormRow>

		{#if $model.describeOther}
			<FormRow>
				<FormColumn>
					<TextArea
						required
						{disabled}
						id="describeOtherDetails"
						label="Please describe"
						bind:value={$model.describeOtherDetails} />
				</FormColumn>
			</FormRow>
		{/if}

		<FormRow>
			<FormColumn cols="12" class="mb-1">
				<p class="label bold">
					Generally, how did you feel when working at your KFC restaurant? (tick all that apply)
				</p>
			</FormColumn>
			<FormColumn cols="12" class="mb-1">
				<CheckboxButton {disabled} id="feel-hot" label="Hot" bind:checked={$model.feelHot} />
			</FormColumn>
			<FormColumn cols="12" class="mb-1">
				<CheckboxButton {disabled} id="feel-stuffy" label="Stuffy" bind:checked={$model.feelStuffy} />
			</FormColumn>
			<FormColumn cols="12" class="mb-1">
				<CheckboxButton
					{disabled}
					id="feel-uncomfortable"
					label="Uncomfortable"
					bind:checked={$model.feelUncomfortable} />
			</FormColumn>
			<FormColumn cols="12" class="mb-1">
				<CheckboxButton {disabled} id="feel-stress" label="Stressed" bind:checked={$model.feelStressed} />
			</FormColumn>
			<FormColumn cols="12" class="mb-1">
				<CheckboxButton {disabled} id="feel-busy" label="Tired" bind:checked={$model.feelTired} />
			</FormColumn>
			<FormColumn cols="12">
				<CheckboxButton {disabled} id="feel-other" label="Other" bind:checked={$model.feelOther} />
			</FormColumn>
		</FormRow>

		{#if $model.feelOther}
			<FormRow>
				<FormColumn>
					<TextArea
						required
						{disabled}
						id="feelOtherDetails"
						label="Please describe"
						bind:value={$model.feelOtherDetails} />
				</FormColumn>
			</FormRow>
		{/if}

		<FormRow class="confirmation-hide">
			<FormColumn class="last">
				<NavigationButtons bind:buttons />
			</FormColumn>
		</FormRow>
	</Form>
</PageSection>

<style>
	.impact {
		margin-top: -40px;
	}

	:global(.confirmation .impact) {
		margin-top: -20px;
	}
</style>
